'use client';

/**
 * DayNav — a day switcher pinned to the top of the itinerary's scroll area:
 *
 *   Days  (1 Sat 17 · Lakes Ent.)  (2 Sun 18 · Eden)  (3 Mon 19 · Sydney)
 *
 * Each chip says where that day ends (and its date, once the trip has one),
 * so you know where a tap lands. The number disc carries the day's colour,
 * the same as its route on the map; the day you're reading is filled.
 *
 * The band is solid white and lifts with a shadow once rows scroll under
 * it. A day becomes "the one you're reading" when its header reaches just
 * under the band, so the chip and the top of the list always agree.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatChipDate, shortPlaceName } from '../../utils/tripFormat';

export interface DayNavItem {
  /** id of the day's <section>. */
  id: string;
  dayNumber: number;
  /** "Melbourne to Lakes Entrance" / "Rest day in Eden" — for the label. */
  description: string;
  /** Where the day ends (the overnight town). */
  place?: string;
  /** YYYY-MM-DD, when the trip has a date. */
  date?: string | null;
  /** The day's colour. */
  color?: string;
  rest?: boolean;
}

/** The nearest scrolling ancestor (the panel's scroll area, or the page). */
export function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
    node = node.parentElement;
  }
  return null;
}

/** Where the band sticks: under any bar the scroll area pins over its top
 *  (the phone sheet's compact trip bar sets --tp-sticky-top). */
function stickyTop(el: HTMLElement): number {
  return parseFloat(getComputedStyle(el).top) || 0;
}

/** How far under the band a day's header may sit and still count as "being read". */
export const ACTIVE_OFFSET = 40;

/** Index of the day being read: the last one whose top has reached the line. */
export function activeDayIndex(tops: number[], line: number): number {
  let active = 0;
  tops.forEach((t, i) => {
    if (t <= line) active = i;
  });
  return active;
}

/** "Sat 17 · Lakes Ent." / "Lakes Ent." / "Rest day". */
export function dayChipLabel(item: Pick<DayNavItem, 'date' | 'place' | 'rest'>): string {
  const date = formatChipDate(item.date);
  const where = item.rest ? 'Rest day' : shortPlaceName(item.place);
  return [date, where].filter(Boolean).join(' · ');
}

const DayNav: React.FC<{
  items: DayNavItem[];
  className?: string;
  /** Pinned under the day chips, in the same band (the backtrack banner). */
  below?: React.ReactNode;
}> = ({ items, className = '', below }) => {
  const navRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [stuck, setStuck] = useState(false);
  /** More chips past the right edge: fade it so the cut reads as "scroll". */
  const [moreRight, setMoreRight] = useState(false);
  const [moreLeft, setMoreLeft] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRow = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const more = row.scrollLeft + row.clientWidth < row.scrollWidth - 2;
    const before = row.scrollLeft > 2;
    setMoreRight((prev) => (prev === more ? prev : more));
    setMoreLeft((prev) => (prev === before ? prev : before));
  }, []);
  useEffect(() => {
    measureRow();
    const row = rowRef.current;
    const ro = typeof ResizeObserver !== 'undefined' && row ? new ResizeObserver(measureRow) : null;
    if (row) ro?.observe(row);
    return () => ro?.disconnect();
  }, [measureRow, items.length]);
  const key = items.map((i) => i.id).join('|');

  const measure = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const scroller = scrollParent(nav);
    const navRect = nav.getBoundingClientRect();
    const areaTop = scroller ? scroller.getBoundingClientRect().top : 0;
    setStuck(!!scroller && scroller.scrollTop > 0 && navRect.top <= areaTop + stickyTop(nav) + 1);
    const line = navRect.bottom + ACTIVE_OFFSET;
    const tops = items.map((i) => document.getElementById(i.id)?.getBoundingClientRect().top ?? Infinity);
    // At the very bottom of the list the last day is the one being read.
    const atEnd = scroller ? scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4 : false;
    const next = atEnd ? items.length - 1 : activeDayIndex(tops, line);
    setActive((prev) => (prev === next ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    let raf = 0;
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0;
          measure();
        });
    };
    const scroller = scrollParent(nav);
    const target: HTMLElement | Window = scroller ?? window;
    measure();
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [measure]);

  // Publish the band's height (it grows when a banner is pinned in it) so
  // day sections keep their headers clear of it when scrolled to.
  useEffect(() => {
    const band = navRef.current;
    const host = band?.parentElement;
    if (!band || !host) return;
    const publish = () => host.style.setProperty('--tp-band-h', `${band.offsetHeight}px`);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(band);
    return () => {
      ro?.disconnect();
      host.style.removeProperty('--tp-band-h');
    };
  }, [items.length]);

  // Keep the active chip in view when there are more days than fit.
  useEffect(() => {
    const pill = navRef.current?.querySelector<HTMLElement>(`[data-day-pill="${active}"]`);
    const row = pill?.parentElement;
    if (!pill || !row || row.scrollWidth <= row.clientWidth) return;
    const left = pill.offsetLeft - row.clientWidth / 2 + pill.offsetWidth / 2;
    row.scrollTo({ left, behavior: 'auto' });
    measureRow();
  }, [active, measureRow]);

  const jump = (index: number) => {
    const el = document.getElementById(items[index].id);
    const nav = navRef.current;
    if (!el || !nav) return;
    const scroller = scrollParent(el);
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const offset = nav.offsetHeight + stickyTop(nav) + 8;
    if (scroller) {
      const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - offset;
      scroller.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    } else {
      el.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    }
    setActive(index);
    el.querySelector<HTMLElement>('h3')?.focus?.({ preventScroll: true });
  };

  if (items.length < 2) return null;

  return (
    <div
      ref={navRef}
      data-testid="day-nav-band"
      data-stuck={stuck || undefined}
      className={`sticky top-[var(--tp-sticky-top,0px)] z-[6] bg-white transition-shadow duration-150 ${
        stuck ? 'shadow-[0_6px_10px_-6px_rgba(22,35,58,.28)]' : ''
      } ${className}`}
    >
      <nav aria-label="Days of the trip" data-testid="day-nav" className="flex h-14 items-center gap-2 border-b border-solid border-x-0 border-t-0 border-tp-line">
        <span aria-hidden="true" className="hidden shrink-0 text-tp-xs font-semibold text-tp-muted sm:inline">
          Days
        </span>
        <div
          ref={rowRef}
          onScroll={measureRow}
          className={`-mx-1 flex min-w-0 items-center gap-1.5 overflow-x-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
            moreLeft && moreRight
              ? '[mask-image:linear-gradient(to_right,transparent,#000_40px,#000_calc(100%-48px),transparent)]'
              : moreRight
                ? '[mask-image:linear-gradient(to_right,#000_calc(100%-48px),transparent)]'
                : moreLeft
                  ? '[mask-image:linear-gradient(to_right,transparent,#000_40px)]'
                  : ''
          }`}
        >
          {items.map((item, i) => {
            const on = i === active;
            const color = item.color ?? '#1558b0';
            const label = dayChipLabel(item);
            return (
              <button
                key={item.id}
                type="button"
                data-day-pill={i}
                aria-current={on ? 'true' : undefined}
                aria-label={`Day ${item.dayNumber}: ${item.description}`}
                title={`Day ${item.dayNumber}: ${item.description}`}
                onClick={() => jump(i)}
                className={`flex h-10 min-h-0 shrink-0 items-center gap-2 rounded-full border-0 py-1 pl-1 pr-3 text-tp-sm font-semibold tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 focus-visible:ring-offset-1 ${
                  on ? 'text-white' : 'bg-white text-tp-ink ring-1 ring-inset ring-tp-line hover:bg-tp-surface'
                }`}
                style={on ? { background: color } : undefined}
              >
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-tp-sm font-bold"
                  style={on ? { background: '#fff', color } : { background: color, color: '#fff' }}
                >
                  {item.dayNumber}
                </span>
                {label && <span className="whitespace-nowrap">{label}</span>}
              </button>
            );
          })}
        </div>
      </nav>
      {below && <div className="border-b border-solid border-x-0 border-t-0 border-tp-line pb-3">{below}</div>}
    </div>
  );
};

export default DayNav;
